import { useEffect, useRef } from 'react';
import { onScroll } from '../utils/scroll.js';
import { usePrefersReducedMotion } from './usePrefersReducedMotion.js';

/**
 * Drifts an element against the scroll by `factor`.
 *
 * `anchor: 'viewport'` (the default) measures from how far the element's centre
 * sits from the viewport centre. That suits anything you scroll past — the hero
 * portrait, the stage photos — because the offset passes through zero as the
 * element crosses the middle of the screen.
 *
 * `anchor: 'page'` measures from the scroll position instead, so the offset is
 * zero at the top of the page and the element starts exactly where the CSS puts
 * it. Use it for decoration pinned near the top of the hero: with the viewport
 * anchor those sit ~40–70px above their stated position at rest, which parked
 * the sky clouds behind the sticky header.
 */
export function useParallax(factor, { anchor = 'viewport' } = {}) {
  const ref = useRef(null);
  const prefersReduced = usePrefersReducedMotion();

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    if (prefersReduced) {
      node.style.transform = '';
      return undefined;
    }

    return onScroll((viewportHeight) => {
      const offset =
        anchor === 'page'
          ? window.scrollY * factor
          : (node.getBoundingClientRect().top +
              node.offsetHeight / 2 -
              viewportHeight / 2) *
            factor;

      node.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
    });
  }, [factor, anchor, prefersReduced]);

  return ref;
}
